import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const BASE = `${API_URL}/admin/commercial/calls`;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// ─── Types ────────────────────────────────────────────────────────────────────

export type CallTaskStatus = "todo" | "called" | "dropped";

export type CallOutcome =
    | "reached"
    | "interested"
    | "callback"
    | "not_interested"
    | "wrong_number"
    | "no_answer";

export type CallDropReason = "regenerated" | "expired" | "manual";

export type CallSource = "weekly_draft" | "manual";

export interface CallNote {
    _id: string;
    text: string;
    authorName: string;
    createdAt: string;
}

/**
 * A row of the calling list. Everything on it is a snapshot taken when the task was created —
 * see `callTaskSchema.ts` for why it is copied rather than joined.
 */
export interface CallTask {
    _id: string;
    establishmentId: string;
    establishmentName: string;
    phone: string | null;
    email: string | null;
    website: string | null;
    address: { street?: string | null; postcode?: string | null; city?: string | null } | null;
    googleUrl: string | null;
    siret: string | null;
    siren: string | null;
    owner: { name: string; age: number | null; establishmentCount: number } | null;
    categories: string[];
    source: CallSource;
    profileId: string | null;
    /** the client this call is for — rendered as the tag on every row */
    profileName: string;
    /** when the client will read about this establishment, for briefs */
    scheduledFor: string | null;
    slot: string | null;
    reason: string | null;
    reasonTag: string | null;
    status: CallTaskStatus;
    outcome: CallOutcome | null;
    /** tries that reached nobody — the row stays in the working list */
    attempts: number;
    lastAttemptAt: string | null;
    notes: CallNote[];
    calledAt: string | null;
    calledBy: string | null;
    createdBy: string | null;
    /** an open task is dropped six days after it appeared */
    expiresAt: string;
    droppedAt: string | null;
    dropReason: CallDropReason | null;
    createdAt: string;
    updatedAt: string;
}

export interface CallProfile {
    _id: string;
    name: string;
    active: boolean;
}

export interface CallListResponse {
    todo: CallTask[];
    history: CallTask[];
    historyTotal: number;
    historyPage: number;
    historyPages: number;
    profiles: CallProfile[];
    stats: { todo: number; called: number; dropped: number };
}

// ─── Calls ────────────────────────────────────────────────────────────────────

export const getCallList = async (params: {
    profileId?: string;
    q?: string;
    page?: number;
    limit?: number;
}): Promise<CallListResponse> => {
    const { data } = await axios.get(BASE, { headers: authHeaders(), params });
    return data;
};

/** Queues an establishment by hand. `profileId` is required — the tag is the point. */
export const addCallTask = async (establishmentId: string, profileId: string): Promise<CallTask> => {
    const { data } = await axios.post(BASE, { establishmentId, profileId }, { headers: authHeaders() });
    return data.task;
};

/** Closes a call: it leaves the working list and joins the history with its notes. */
export const markCalled = async (
    id: string,
    outcome: CallOutcome,
    note?: string
): Promise<CallTask> => {
    const { data } = await axios.patch(
        `${BASE}/${id}`,
        { status: "called", outcome, note },
        { headers: authHeaders() }
    );
    return data.task;
};

/** Puts a closed row back at the top of the working list, with a fresh six-day clock. */
export const reopenCallTask = async (id: string): Promise<CallTask> => {
    const { data } = await axios.patch(`${BASE}/${id}`, { status: "todo" }, { headers: authHeaders() });
    return data.task;
};

/** Nobody picked up: bumps the attempt counter, the row stays where it is. */
export const logCallAttempt = async (id: string, note?: string): Promise<CallTask> => {
    const { data } = await axios.post(`${BASE}/${id}/attempt`, { note }, { headers: authHeaders() });
    return data.task;
};

export const addCallNote = async (id: string, text: string): Promise<CallTask> => {
    const { data } = await axios.post(`${BASE}/${id}/notes`, { text }, { headers: authHeaders() });
    return data.task;
};

export const dropCallTask = async (id: string): Promise<void> => {
    await axios.delete(`${BASE}/${id}`, { headers: authHeaders() });
};

// ─── Labels ───────────────────────────────────────────────────────────────────

export const OUTCOME_LABEL: Record<CallOutcome, string> = {
    reached: "Contact établi",
    interested: "Intéressé",
    callback: "À rappeler",
    not_interested: "Pas intéressé",
    wrong_number: "Injoignable",
    no_answer: "Sans réponse",
};

export const DROP_REASON_LABEL: Record<CallDropReason, string> = {
    regenerated: "Remplacé par la nouvelle liste",
    expired: "Expiré après 6 jours",
    manual: "Retiré à la main",
};
